/*
 * Copyright (c) 2025 Sila Artificial Intelligence Research LLC
 * A DA Holding Ltd Company
 * All rights reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 * This software and its documentation are proprietary to Sila Artificial
 * Intelligence Research LLC. Unauthorized copying, distribution, or use
 * of this software is strictly prohibited.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 * THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING
 * FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER
 * DEALINGS IN THE SOFTWARE.
 */

import React, { useState, useRef, useEffect } from 'react';
import { useDateFilter } from '../contexts/DateFilterContext';

export interface DemographicFilters {
  age: string[];
  gender: string[];
  nationality: string[];
  city: string[];
  sec: string[];
  decisionMaker: string[];
  householdCars: string[];
  residenceType: string[];
}

interface DemographicFilterProps {
  onFiltersChange: (filters: DemographicFilters) => void;
  availableFilters?: {
    age: string[];
    gender: string[];
    nationality: string[];
    city: string[];
    sec: string[];
    decisionMaker: string[];
    householdCars: string[];
    residenceType: string[];
  };
}

/**
 * Advanced demographic filtering component for brand health data
 */
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const DemographicFilter: React.FC<DemographicFilterProps> = ({
  onFiltersChange,
  availableFilters = {
    age: ['16-25', '26-35', '36-45', '45-99'],
    gender: ['Male', 'Female'],
    nationality: ['Saudi', 'Arab Expat', 'Asian Expat', 'Other'],
    city: ['Riyadh', 'Jeddah', 'Dammam', 'Makkah', 'Madinah', 'Other'],
    sec: ['SEC A', 'SEC B', 'SEC C1', 'SEC C2', 'SEC D'],
    decisionMaker: ['Myself', 'Spouse', 'Parent', 'Other'],
    householdCars: ['No car', '1 car', '2 cars', '3+ cars'],
    residenceType: ['Villa', 'Flat', 'Traditional House', 'Other']
  }
}) => {
  const { selectedDate, setSelectedDate, minDate, maxDate, formatDate } = useDateFilter();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('time');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [viewMonth, setViewMonth] = useState(selectedDate.getMonth());
  const [viewYear, setViewYear] = useState(selectedDate.getFullYear());
  const [selectedFilters, setSelectedFilters] = useState<DemographicFilters>({
    age: [],
    gender: [],
    nationality: [],
    city: [],
    sec: [],
    decisionMaker: [],
    householdCars: [],
    residenceType: []
  });

  // Update view when selected date changes
  useEffect(() => {
    setViewMonth(selectedDate.getMonth());
    setViewYear(selectedDate.getFullYear());
  }, [selectedDate]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filterCategories = [
    { id: 'time', label: 'Time', icon: 'fa-calendar-alt' },
    { id: 'demographics', label: 'Demographics', icon: 'fa-users' },
    { id: 'geographic', label: 'Geographic', icon: 'fa-map-marker-alt' },
    { id: 'economic', label: 'Economic', icon: 'fa-chart-line' },
    { id: 'household', label: 'Household', icon: 'fa-home' }
  ];

  // Date filter helper functions
  const getDaysInMonth = (month: number, year: number): number => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month: number, year: number): number => {
    return new Date(year, month, 1).getDay();
  };

  const isDateInRange = (date: Date): boolean => {
    return date >= minDate && date <= maxDate;
  };

  const isDateSelected = (day: number): boolean => {
    return (
      selectedDate.getDate() === day &&
      selectedDate.getMonth() === viewMonth &&
      selectedDate.getFullYear() === viewYear
    );
  };

  const handleDateSelect = (day: number) => {
    const newDate = new Date(viewYear, viewMonth, day);
    if (isDateInRange(newDate)) {
      setSelectedDate(newDate);
    }
  };

  const canGoToPrevMonth = (): boolean => {
    const prevMonth = new Date(viewYear, viewMonth - 1, 1);
    return prevMonth >= new Date(minDate.getFullYear(), minDate.getMonth(), 1);
  };

  const canGoToNextMonth = (): boolean => {
    const nextMonth = new Date(viewYear, viewMonth + 1, 1);
    return nextMonth <= new Date(maxDate.getFullYear(), maxDate.getMonth(), 1);
  };

  const handlePrevMonth = () => {
    if (canGoToPrevMonth()) {
      if (viewMonth === 0) {
        setViewMonth(11);
        setViewYear(viewYear - 1);
      } else {
        setViewMonth(viewMonth - 1);
      }
    }
  };

  const handleNextMonth = () => {
    if (canGoToNextMonth()) {
      if (viewMonth === 11) {
        setViewMonth(0);
        setViewYear(viewYear + 1);
      } else {
        setViewMonth(viewMonth + 1);
      }
    }
  };

  const renderCalendarDays = () => {
    const daysInMonth = getDaysInMonth(viewMonth, viewYear);
    const firstDay = getFirstDayOfMonth(viewMonth, viewYear);
    const days: React.ReactNode[] = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="h-8 w-8" />
      );
    }

    // Actual days
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(viewYear, viewMonth, day);
      const inRange = isDateInRange(date);
      const selected = isDateSelected(day);
      const isToday = new Date().toDateString() === date.toDateString();

      days.push(
        <button
          key={day}
          onClick={() => handleDateSelect(day)}
          disabled={!inRange}
          className={`h-8 w-8 rounded-full text-sm font-medium transition-all ${
            selected
              ? 'bg-primary text-white'
              : inRange
              ? 'hover:bg-primary/20 text-foreground'
              : 'text-text-muted/30 cursor-not-allowed'
          } ${isToday && !selected ? 'ring-1 ring-primary' : ''}`}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  const getFiltersByCategory = (category: string) => {
    switch (category) {
      case 'demographics':
        return ['age', 'gender', 'nationality'];
      case 'geographic':
        return ['city'];
      case 'economic':
        return ['sec', 'householdCars'];
      case 'household':
        return ['decisionMaker', 'residenceType'];
      default:
        return [];
    }
  };

  const toggleFilter = (category: keyof DemographicFilters, value: string) => {
    const newFilters = { ...selectedFilters };
    const index = newFilters[category].indexOf(value);
    
    if (index > -1) {
      newFilters[category] = newFilters[category].filter(v => v !== value);
    } else {
      newFilters[category] = [...newFilters[category], value];
    }
    
    setSelectedFilters(newFilters);
    onFiltersChange(newFilters);
  };

  const clearFilters = () => {
    const emptyFilters: DemographicFilters = {
      age: [],
      gender: [],
      nationality: [],
      city: [],
      sec: [],
      decisionMaker: [],
      householdCars: [],
      residenceType: []
    };
    setSelectedFilters(emptyFilters);
    onFiltersChange(emptyFilters);
  };

  const getActiveFilterCount = () => {
    return Object.values(selectedFilters).reduce((acc, filters) => acc + filters.length, 0);
  };

  const getFilterLabel = (key: string): string => {
    const labels: Record<string, string> = {
      age: 'Age Group',
      gender: 'Gender',
      nationality: 'Nationality',
      city: 'City',
      sec: 'Socio-Economic Class',
      decisionMaker: 'Purchase Decision Maker',
      householdCars: 'Household Cars',
      residenceType: 'Residence Type'
    };
    return labels[key] || key;
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Filter Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-2.5 py-1 border border-border/50 rounded-full hover:border-primary/40 hover:bg-primary/5 transition-all"
      >
        <i className="fas fa-sliders-h text-text-muted text-xs"></i>
        <span className="text-foreground text-sm">Filters</span>
        {getActiveFilterCount() > 0 && (
          <span className="w-4 h-4 bg-primary text-white text-[10px] rounded-full flex items-center justify-center">
            {getActiveFilterCount()}
          </span>
        )}
      </button>

      {/* Filter Dropdown Panel */}
      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-96 bg-card border border-border rounded-xl shadow-2xl overflow-hidden z-dropdown max-h-[600px] flex flex-col">
            {/* Header */}
            <div className="p-6 border-b border-[var(--border)]">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-foreground">Advanced Filters</h2>
                <button
                  onClick={() => setIsOpen(false)}
                  className="p-2 hover:bg-background-tertiary rounded-lg transition-colors"
                >
                  <i className="fas fa-times text-text-muted" />
                </button>
              </div>

              {getActiveFilterCount() > 0 && (
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-sm text-text-muted">
                    {getActiveFilterCount()} filter{getActiveFilterCount() !== 1 ? 's' : ''} applied
                  </span>
                  <button
                    onClick={clearFilters}
                    className="text-sm text-primary hover:text-primary/80 transition-colors"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </div>

            {/* Category Tabs */}
            <div className="flex border-b border-[var(--border)]">
              {filterCategories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setActiveTab(category.id)}
                  className={`flex-1 px-4 py-3 text-sm font-medium transition-all ${
                    activeTab === category.id
                      ? 'text-primary border-b-2 border-primary bg-primary/5'
                      : 'text-text-muted hover:text-foreground hover:bg-background-tertiary'
                  }`}
                >
                  <i className={`fas ${category.icon} mr-2`}></i>
                  {category.label}
                </button>
              ))}
            </div>

            {/* Filter Options */}
            <div className="flex-1 overflow-y-auto p-6">
              {/* Time Period / Calendar View */}
              {activeTab === 'time' && (
                <div>
                  <h3 className="text-sm font-semibold text-text-secondary uppercase mb-4">
                    Select Data Period
                  </h3>

                  {/* Month/Year Header */}
                  <div className="flex items-center justify-between mb-4">
                    <button
                      onClick={handlePrevMonth}
                      disabled={!canGoToPrevMonth()}
                      className={`p-1.5 rounded-lg transition-colors ${
                        canGoToPrevMonth()
                          ? 'hover:bg-background-secondary text-foreground'
                          : 'text-text-muted/30 cursor-not-allowed'
                      }`}
                    >
                      <i className="fas fa-chevron-left text-sm"></i>
                    </button>
                    <span className="text-lg font-semibold text-foreground">
                      {MONTH_NAMES[viewMonth]} {viewYear}
                    </span>
                    <button
                      onClick={handleNextMonth}
                      disabled={!canGoToNextMonth()}
                      className={`p-1.5 rounded-lg transition-colors ${
                        canGoToNextMonth()
                          ? 'hover:bg-background-secondary text-foreground'
                          : 'text-text-muted/30 cursor-not-allowed'
                      }`}
                    >
                      <i className="fas fa-chevron-right text-sm"></i>
                    </button>
                  </div>

                  {/* Day Names */}
                  <div className="grid grid-cols-7 gap-1 mb-2">
                    {DAY_NAMES.map(day => (
                      <div key={day} className="h-8 w-8 flex items-center justify-center text-xs text-text-muted font-medium">
                        {day}
                      </div>
                    ))}
                  </div>

                  {/* Calendar Grid */}
                  <div className="grid grid-cols-7 gap-1">
                    {renderCalendarDays()}
                  </div>

                  {/* Data Range Info */}
                  <div className="mt-4 pt-3 border-t border-border">
                    <p className="text-xs text-text-muted text-center">
                      Data available: June 1, 2025 - December 31, 2025
                    </p>
                  </div>
                </div>
              )}

              {/* Other Filter Tabs */}
              {activeTab !== 'time' && getFiltersByCategory(activeTab).map(filterKey => {
                const key = filterKey as keyof DemographicFilters;
                const options = availableFilters[key];

                return (
                  <div key={filterKey} className="mb-6">
                    <h3 className="text-sm font-semibold text-text-secondary uppercase mb-3">
                      {getFilterLabel(filterKey)}
                    </h3>
                    <div className="space-y-2">
                      {[...new Set(options)].map(option => (
                        <label
                          key={option}
                          className="flex items-center space-x-3 cursor-pointer hover:bg-background-tertiary p-2 rounded-lg transition-colors"
                        >
                          <input
                            type="checkbox"
                            checked={selectedFilters[key].includes(option)}
                            onChange={() => toggleFilter(key, option)}
                            className="w-4 h-4 text-primary bg-background-tertiary border-[var(--border)] rounded focus:ring-primary focus:ring-2"
                          />
                          <span className="text-foreground text-sm">{option}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Apply Button */}
            <div className="p-6 border-t border-[var(--border)]">
              <button
                onClick={() => setIsOpen(false)}
                className="w-full py-3 bg-gradient-primary text-white rounded-lg hover:opacity-90 transition-all font-medium"
              >
                Apply Filters
              </button>
            </div>
        </div>
      )}
    </div>
  );
};

export default DemographicFilter;